import { faDownload, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState } from "react";

import { CenteredModal } from "./CenteredModal";
import { H5 } from "./base/TextComponents/H5";
import { IconButton } from "./buttons/IconButton";
import { Loading } from "./base/Loading";
import { P1B } from "./base/TextComponents/P1B";
import { PdfViewer } from "./PdfViewer";
import { downloadFile } from "./helpers";

const supportedImgExtensions = [".jpg", ".jpeg", ".png", ".svg"];

type FileNameUrl = {
  fileName: string;
  url: string;
};

interface Props {
  // accept either a file or a URL/filename combination
  file?: File;
  fileNameUrl?: FileNameUrl;
  hideFileViewer: () => void;
}

export const FileViewerModal = (props: Props) => {
  const [error, setError] = useState<boolean>(false);
  const [url, setUrl] = useState<string>("");
  const [imageLoading, setImageLoading] = useState(true);

  const fileName = props.fileNameUrl?.fileName || props.file?.name || "";

  const fileType = fileName.toLowerCase().endsWith(".pdf")
    ? "pdf"
    : supportedImgExtensions.some((ext) => fileName.toLowerCase().endsWith(ext))
      ? "img"
      : null;

  useEffect(() => {
    // populate the file name and type and url
    // based on the props passed in
    // if file is passed in, use file properties

    if (fileType == null) {
      setError(true);
    } else if (fileType == "pdf" || fileType == "img") {
      setError(false);
    }

    let objectUrl: string | null = null;

    if (props.file) {
      objectUrl = URL.createObjectURL(props.file);
      setUrl(objectUrl);
    } else if (props.fileNameUrl?.url) {
      setUrl(props.fileNameUrl?.url);
    }

    // Cleanup the created object URL when component unmounts or when props change
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [props.file, props.fileNameUrl]);

  return (
    <CenteredModal
      className="tw-max-h-[95vh] tw-overflow-y-auto"
      hideModal={props.hideFileViewer}
    >
      <div className="tw-bg-gray-light-3">
        <div className="tw-flex tw-items-center tw-border-b tw-border-l-0 tw-border-r-0 tw-border-t-0 tw-border-solid tw-border-gray-5 tw-bg-white tw-px-10 tw-py-6">
          <P1B className="tw-mb-0">{fileName}</P1B>
          <IconButton
            className="tw-ml-auto tw-text-[24px] tw-text-gray-3"
            onClick={() => props.hideFileViewer()}
            fontAwesomeIcon={faXmark}
          />
        </div>
        <div className="tw-flex tw-justify-center tw-bg-gray-light-3">
          {error ? (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center">
              <div>
                <H5>We weren't able to preview your file.</H5>
                <H5>Try downloading it below.</H5>
              </div>
              <IconButton
                className="tw-mt-8 tw-h-[60px] tw-w-[60px] tw-text-[36px] tw-text-blue-1"
                fontAwesomeIcon={faDownload}
                onClick={() => downloadFile(url, fileName)}
              />
            </div>
          ) : (
            <div>
              {fileType === "pdf" ? (
                <PdfViewer
                  file={props.file || props.fileNameUrl?.url || ""}
                  setPdfError={setError}
                />
              ) : (
                fileType === "img" &&
                url && (
                  <div>
                    {imageLoading && (
                      <Loading className="tw-my-[20px]" size={48} />
                    )}
                    <img
                      onLoad={() => setImageLoading(false)}
                      onError={() => {
                        setImageLoading(false);
                        setError(true);
                      }}
                      src={url}
                    />
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </div>
    </CenteredModal>
  );
};
